/**
 * D454 step 3: the words and shapes of arranging the farm. Pure: the store, the mode bar, the
 * dock and the producer rows all read these, and nothing here draws. Theme words come from
 * `terms.ts` and the content (`arrange.places`), never from the farm.
 */
import type { IdleContent } from '@hedgerow/data';
import {
  idleLayout,
  idleLinkBonuses,
  idleLinks,
  type IdleLayout,
  type IdleLayoutRect,
  type IdleMoveCheck,
  type IdleMoveTarget,
  type IdleRefusal,
  type IdleWorldState,
} from '@hedgerow/sim';
import { cap, t as term } from './terms.ts';

const pct = (f: number) => `${Math.round(f * 100)}%`;
const lc = (s: string) => s.toLowerCase();

export const nameOf = (content: IdleContent, id: string): string =>
  content.producers.find((p) => p.id === id)?.name ?? id;

/** `hens` -> `hens'`, `wheat` -> `wheat's`. */
export const possessive = (s: string): string => (/s$/i.test(s) ? `${s}'` : `${s}'s`);

/** The theme's word for a producer's plot: `field`, `pen`; `plot` when the theme has none. */
export function placeOf(content: IdleContent, id: string): string {
  const g = content.producers.find((p) => p.id === id)?.group;
  return (g && content.arrange.places?.[g]) || 'plot';
}

/** A link chip on a producer row's note line (item 270). */
export interface LinkChip {
  /** `gain`: fed or pollinated by a neighbour; `give`: feeds one; `yard`: in the hands' yard. */
  role: 'gain' | 'give' | 'yard';
  text: string;
}

/**
 * The link chips for each open producer, from where the plots sit now: what it gains, what it
 * gives, and the yard. Percentages come from the sim, never typed here.
 */
export function linkChips(
  state: IdleWorldState,
  content: IdleContent,
  layout?: IdleLayout,
): Record<string, LinkChip[]> {
  const out: Record<string, LinkChip[]> = {};
  const add = (id: string, c: LinkChip) => (out[id] ??= []).push(c);
  for (const l of idleLinks(state, content, layout)) {
    const from = nameOf(content, l.from);
    const to = nameOf(content, l.to);
    add(l.to, {
      role: 'gain',
      text:
        l.kind === 'feeds'
          ? `Fed by ${lc(from)} next door +${pct(l.bonus)}`
          : `${from} next door +${pct(l.bonus)}`,
    });
    add(l.from, {
      role: 'give',
      text: `${l.kind === 'feeds' ? 'Feeds' : 'Pollinates'} the ${lc(to)}`,
    });
  }
  const bonuses = idleLinkBonuses(state, content, layout);
  for (const p of content.producers) {
    if (bonuses[p.id]?.yard)
      add(p.id, { role: 'yard', text: `In the ${term('yard')} +${pct(content.arrange.yard)}` });
  }
  return out;
}

/** The mode bar's two lines: nothing held, or a plot held (`held` is the producer's id). */
export function arrangeBarText(
  content: IdleContent,
  held: string | null,
): { title: string; sub: string } {
  const places = [...new Set(Object.values(content.arrange.places ?? {}))];
  if (held)
    return {
      title: `Moving ${lc(nameOf(content, held))}`,
      sub:
        places.length === 2
          ? `Drop it on a ${places[0]} or ${places[1]}`
          : 'Drop it on another plot',
    };
  return {
    title: `Arranging the ${term('world')}`,
    sub:
      places.length === 2
        ? `Tap a ${places[0]} or ${places[1]} to pick it up`
        : 'Tap a plot to pick it up',
  };
}

/** The words for the arrange sheet tile that is locked before the move generation. */
export const arrangeLockedText = (): string => `Arrange opens in ${term('generation')} 2.`;

/** The one-time key's lead line and its three items. */
export const ARRANGE_KEY = {
  lead: (): string =>
    `Moving is free. A crop next to the animals it feeds pays them more. The ring shows ${aHand()} ${term('yard')}.`,
  items: (): string[] => ['Where plots swap', 'A link now', `A ${term('hand')}'s ${term('yard')}`],
};
const aHand = (): string => `a ${term('hand')}'s`;

const overlaps = (a: IdleLayoutRect, b: IdleLayoutRect) =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

/** Why a free-ground spot is not allowed, in the most specific words the layout allows. */
export function spotReason(
  state: IdleWorldState,
  content: IdleContent,
  producer: string,
  target: { x: number; y: number },
): string {
  const layout = idleLayout(state, content);
  const own = layout.plots[producer];
  const r = { x: target.x, y: target.y, w: own?.w ?? 1, h: own?.h ?? 1 };
  if (overlaps(r, layout.yard)) return `The ${term('yard')} stays where it is`;
  if (layout.pond && overlaps(r, layout.pond)) return 'The pond stays where it is';
  for (const [id, p] of Object.entries(layout.plots))
    if (id !== producer && overlaps(r, p)) return 'Another plot is in the way';
  return 'Not on the land';
}

/** What a refused move says. `with` names the plot dropped on, for the two size reasons. */
export function moveRefusal(
  content: IdleContent,
  reason: IdleRefusal,
  producer?: string,
  other?: string,
): string | null {
  switch (reason) {
    case 'too_early':
      return arrangeLockedText();
    case 'no_plot':
      return 'No plot to move there';
    case 'bad_spot':
      return 'Nothing can go there';
    case 'too_big': {
      if (!other) return 'Too big for that place';
      const n = lc(nameOf(content, other));
      return `Too big for the ${possessive(n)} ${placeOf(content, other)}`;
    }
    case 'other_too_big': {
      if (!other) return 'The other plot is too big for this place';
      const o = lc(nameOf(content, other));
      const mine = producer ? lc(nameOf(content, producer)) : 'this plot';
      return `The ${possessive(o)} ${placeOf(content, other)} is too big for ${possessive(mine)} place`;
    }
    default:
      return null;
  }
}

/** The dock under the map while a plot is held and a spot is under the finger. */
export interface DockView {
  heading: string;
  lines: string[];
  blocked: boolean;
  /** A swap: each plot on its own line, for the dock to draw with its icon and its change. */
  rows?: DockRow[];
}

/** One plot of a swap: where it goes, why, and what it gains or loses (a fraction). */
export interface DockRow {
  producer: string;
  /** `Wheat to the pigs' place`; null for a third plot whose links change. */
  where: string | null;
  why: string;
  change: number;
}

/** The change as the dock shows it: `+10%`, `−10%`, `0%`. */
export const changeText = (f: number): string =>
  f > 0 ? `+${pct(f)}` : f < 0 ? `−${pct(-f)}` : '0%';

/**
 * What the dock says for `producer` over `target`: gains and losses together, both plots of a
 * swap, "No links here" for a free spot that makes none, or why the spot cannot take it.
 * `state` is the world as drawn (the draft layout), `check` its `idleMoveCheck`.
 */
export function dockView(
  state: IdleWorldState,
  content: IdleContent,
  producer: string,
  target: IdleMoveTarget,
  check: IdleMoveCheck,
): DockView {
  if (!check.ok) {
    const reason =
      check.reason === 'bad_spot' && 'x' in target
        ? spotReason(state, content, producer, target)
        : (moveRefusal(
            content,
            check.reason,
            producer,
            check.with ?? ('with' in target ? target.with : undefined),
          ) ?? 'Nothing can go there');
    return { heading: 'Not here', lines: [reason], blocked: true };
  }
  const mine = nameOf(content, producer);
  const lines: string[] = [];
  if (check.with !== undefined) return swapView(state, content, producer, check.with, check);
  const gainedLinks = check.changes.gained;
  const sentence = (verb: 'next to' | 'leaves', l: { from: string; to: string; bonus: number }) =>
    verb === 'next to'
      ? `${nameOf(content, l.from)} next to the ${lc(nameOf(content, l.to))}. ${nameOf(content, l.to)} +${pct(l.bonus)}.`
      : `${nameOf(content, l.from)} leaves the ${lc(nameOf(content, l.to))}. ${nameOf(content, l.to)} −${pct(l.bonus)}.`;
  for (const l of gainedLinks) lines.push(sentence('next to', l));
  for (const l of check.changes.lost) lines.push(sentence('leaves', l));
  // The yard: whoever it newly reaches or no longer reaches.
  const before = idleLinkBonuses(state, content);
  const after = idleLinkBonuses(state, content, check.layout);
  let yardGain = false;
  for (const p of content.producers) {
    const was = before[p.id]?.yard ?? false;
    const is = after[p.id]?.yard ?? false;
    if (was === is) continue;
    if (is) yardGain = true;
    lines.push(
      is
        ? `In the ${term('yard')}: ${nameOf(content, p.id)} +${pct(content.arrange.yard)}.`
        : `Leaves the ${term('yard')}: ${nameOf(content, p.id)} −${pct(content.arrange.yard)}.`,
    );
  }
  if (gainedLinks.length === 0 && !yardGain) lines.unshift('No links here');
  return { heading: `${cap(mine)} here`, lines, blocked: false };
}

/** The dock for a swap: a heading and one row for each plot that changes. */
function swapView(
  state: IdleWorldState,
  content: IdleContent,
  a: string,
  b: string,
  check: Extract<IdleMoveCheck, { ok: true }>,
): DockView {
  const before = idleLinkBonuses(state, content);
  const after = idleLinkBonuses(state, content, check.layout);
  const { gained, lost, bonuses } = check.changes;
  const change = (id: string) => {
    const c = bonuses.find((x) => x.producer === id);
    return c ? c.after - c.before : 0;
  };
  const yard = term('yard');
  const why = (id: string, own: boolean): string => {
    const parts: string[] = [];
    const was = before[id]?.yard ?? false;
    const is = after[id]?.yard ?? false;
    if (was !== is) parts.push(is ? `Into the ${yard}` : `Leaves the ${yard}`);
    const partner = (l: { from: string; to: string }) => (l.from === id ? l.to : l.from);
    for (const l of gained)
      if (l.from === id || l.to === id) parts.push(`next to ${lc(nameOf(content, partner(l)))}`);
    for (const l of lost)
      if (l.from === id || l.to === id) parts.push(`leaves the ${lc(nameOf(content, partner(l)))}`);
    if (parts.length === 0) return own ? 'No new links' : 'Its links change';
    const text = parts.join('; ');
    return text.charAt(0).toUpperCase() + text.slice(1);
  };
  const row = (id: string, other: string): DockRow => ({
    producer: id,
    where: `${nameOf(content, id)} to the ${possessive(lc(nameOf(content, other)))} place`,
    why: why(id, true),
    change: change(id),
  });
  const rows = [row(a, b), row(b, a)];
  for (const c of bonuses)
    if (c.producer !== a && c.producer !== b)
      rows.push({
        producer: c.producer,
        where: null,
        why: why(c.producer, false),
        change: change(c.producer),
      });
  return {
    heading: `Swap ${lc(nameOf(content, a))} and ${lc(nameOf(content, b))}`,
    lines: rows.map(
      (r) => `${r.where ?? nameOf(content, r.producer)}. ${r.why}. ${changeText(r.change)}`,
    ),
    blocked: false,
    rows,
  };
}
